/**
 * UX-342: keyboard-operable desktop disclosure menus.
 *
 * Prestige's follow-summary-link behavior preserves pointer clicks as parent
 * navigation. This enhancement reserves Enter and Space for disclosure
 * operation, keeps aria-expanded synchronized, and restores trigger focus
 * after Escape without changing hover, click, or touch handlers.
 */
(() => {
  const disclosureSelector = 'mega-menu-disclosure, dropdown-menu-disclosure';
  const summarySelector = 'mega-menu-disclosure summary, dropdown-menu-disclosure summary';
  const openDetailsSelector = 'mega-menu-disclosure > details[open], dropdown-menu-disclosure > details[open]';
  const initialized = new WeakSet();

  const parts = (disclosure) => {
    const details = Array.from(disclosure.children).find((child) => child.matches?.('details'));
    const summary = details?.querySelector(':scope > summary');

    return { details, summary };
  };

  const syncExpanded = (details, summary) => {
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  const closeOtherMenus = (currentDetails) => {
    document.querySelectorAll(openDetailsSelector).forEach((details) => {
      if (details === currentDetails || details.contains(currentDetails)) return;

      details.open = false;
      const summary = details.querySelector(':scope > summary');
      if (summary) syncExpanded(details, summary);
    });
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.(disclosureSelector).forEach((disclosure) => {
      if (initialized.has(disclosure)) return;

      const { details, summary } = parts(disclosure);
      if (!details || !summary) return;

      initialized.add(disclosure);
      syncExpanded(details, summary);
      details.addEventListener('toggle', () => syncExpanded(details, summary));
    });
  };

  document.addEventListener('keydown', (event) => {
    const summary = event.target.closest?.(summarySelector);
    const disclosure = summary?.closest(disclosureSelector);

    if (
      disclosure &&
      summary === parts(disclosure).summary &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.repeat &&
      (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const { details } = parts(disclosure);
      const opening = !details.open;

      if (opening) closeOtherMenus(details);
      details.open = opening;
      syncExpanded(details, summary);
      return;
    }

    if (event.key !== 'Escape') return;

    const openDisclosure = event.target.closest?.(disclosureSelector);
    if (!openDisclosure) return;

    const { details, summary: trigger } = parts(openDisclosure);
    if (!details?.open || !trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    details.open = false;
    syncExpanded(details, trigger);
    trigger.focus();
  }, true);

  initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches?.(disclosureSelector)) initialize(node.parentElement || document);
        else initialize(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
